import React, { ReactNode, useState } from 'react';

import { Box, Button, Flex, Stack, createStyles } from '@mantine/core';
import { IconReportSearch, IconRocket } from '@tabler/icons-react';
import { BackButton } from 'components/BackButton';
import { RawResourceTextModal } from 'components/RawResourceTextModal/RawResourceTextModal';
import { ResourcePlaygroundDrawer } from 'pages/ManageSmartAssistant/Playground/ResourcePlaygroundDrawer';
import { useTranslation } from 'react-i18next';
import { Z_INDEX } from 'shared/constants/z-index';

interface ResourceViewerWrapperProps {
  resourceId: string;
  backgroundColor: string;
  fileName?: string;
  children: ReactNode;
}

const useStyles = createStyles((theme) => ({
  container: {
    minHeight: '100vh',
    overflow: 'auto',
    backgroundColor: theme.colors.gray[0]
  },
  leftControls: {
    position: 'fixed',
    top: 15,
    left: 15,
    zIndex: Z_INDEX.FLOATING_ELEMENTS
  },
  rightControls: {
    position: 'fixed',
    top: 15,
    right: 15,
    zIndex: Z_INDEX.FLOATING_ELEMENTS
  }
}));

export function ResourceViewerWrapper({ resourceId, fileName, children, backgroundColor }: ResourceViewerWrapperProps) {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const [rawTextModalOpened, setRawTextModalOpened] = useState(false);
  const handleRawTextClick = () => setRawTextModalOpened(true);
  const handleModalClose = () => setRawTextModalOpened(false);

  const [resourcePlaygroundDrawerOpened, setResourcePlaygroundDrawerOpened] = useState(false);
  const handleResourcePlaygroundDrawerClose = () => setResourcePlaygroundDrawerOpened(false);
  const handleResourcePlaygroundDrawerOpen = () => setResourcePlaygroundDrawerOpened(true);

  return (
    <>
      <Stack className={classes.container} sx={{ backgroundColor: backgroundColor }}>
        <Box className={classes.leftControls}>
          <BackButton bg={backgroundColor} />
        </Box>

        <Flex className={classes.rightControls} gap="sm">
          <Button leftIcon={<IconRocket size={16} />} onClick={handleResourcePlaygroundDrawerOpen}>
            {t('APP.ACTIONS.TEST')}
          </Button>

          <Button leftIcon={<IconReportSearch size={16} />} onClick={handleRawTextClick}>
            {t('RESOURCE_VIEWER_WRAPPER.VIEW_RAW_TEXT')}
          </Button>
        </Flex>

        {children}
      </Stack>

      <ResourcePlaygroundDrawer
        resourceId={resourceId}
        opened={resourcePlaygroundDrawerOpened}
        onClose={handleResourcePlaygroundDrawerClose}
      />

      <RawResourceTextModal
        resourceId={resourceId}
        opened={rawTextModalOpened}
        onClose={handleModalClose}
        title={fileName || t('RESOURCE_VIEWER_WRAPPER.RAW_TEXT_CONTENT')}
      />
    </>
  );
}
